import { useEffect, useMemo, useRef, useState } from "react";
import track from "../services/track.service";
import { Button, Checkbox, message, Modal, Spin } from 'antd';
import Loader from "../components/loader";
import tracks_service from "../services/track.service";
import { LoadingOutlined } from '@ant-design/icons';

const { confirm } = Modal;

/* -------------------------------------------------------------------------------- *\
|                               RaceCentralizer
\* -------------------------------------------------------------------------------- */
export default function RaceCentralizer()
{
    const [loading, setLoading] = useState(true);
    const [showDelete, setShowDelete] = useState(false);
    const [tracks,setTracks] = useState([]);
    const [actionQueue, setActionQueue] = useState([]);   // pending tracks to sync
    const [currentAction, setCurrentAction] = useState(null); // track being synced right now
    const hasProcessedRef = useRef(false);

    const isBusy = currentAction !== null || actionQueue.length > 0;

    const codeColWidth = useMemo(() =>
    {
        const longest = tracks.reduce((max, t) => Math.max(max, (t.code || '').length), 'Code'.length);
        return longest * 8 + 16; // approx char width + padding
    }, [tracks]);

    /* -------------------------------------------------------------------------------- *\
    |                               getstate
    \* -------------------------------------------------------------------------------- */
    function getstate(t)
    {
        if (!t.local)   return "not in local";
        if (!t.central) return "not in central";
        if (t.local.hash !== t.central.hash) return "different";
        if (t.local.distance !== t.central.distance) return "distance";
        if (t.local.type !== t.central.type) return "type";
        if (t.local.hash === t.central.hash) return "same";
        return "different";
    }

    /* -------------------------------------------------------------------------------- *\
    |                               refreshAll
    \* -------------------------------------------------------------------------------- */
    function refreshAll()
    {
        setLoading(true);
        track.getSynchro()
            .then((answer) =>
            {
                const newtracks = [];
                for (const t of answer.data)
                {
                    newtracks.push(
                    {
                        code: t.code,
                        sic: t.sic,
                        local: t.local,
                        central: t.central,
                        same: t.local && t.central && t.local.hash === t.central.hash && t.local.distance === t.central.distance && t.local.type === t.central.type,
                        state: getstate(t)
                    });
                }
                setTracks(newtracks);
            })
            .catch((error) => { setTracks([]); message.error(error.message || "Internal error"); })
            .finally(() => setLoading(false));
    }

    /* -------------------------------------------------------------------------------- *\
    |                               downloadcsv
    \* -------------------------------------------------------------------------------- */
    function downloadcsv(t, local)
    {
        if (local && !t.local) return;
        if (!local && !t.central) return;

        const req = local ? tracks_service.getContent(t.local.uuid) : tracks_service.getContentCentral(t.sic, t.code);
        req.then(r =>
        {
            if (!r.data || !r.data.content) return;

            const blob = new Blob([r.data.content], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = local
                ? `${t.sic}_${t.code}_${t.local.type}_${t.local.distance}_${t.code}_local.csv`
                : `${t.sic}_${t.code}_${t.central.type}_${t.central.distance}_${t.code}_central.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        })
        .catch(error => { message.error(error); });
    }

    /* -------------------------------------------------------------------------------- *\
    |                               deleteLocal
    \* -------------------------------------------------------------------------------- */
    function deleteLocal(t)
    {
        if (!t.local) return;
        confirm({
            title: 'Delete local track ?',
            content: `This will definitely delete ${t.sic} ${t.code} from local.`,
            onOk()
            {
                queueDelete(t);
            },
            onCancel() {},
        });
    }

    /* -------------------------------------------------------------------------------- *\
    |                               queueSync  — ajoute à la file sans exécuter
    \* -------------------------------------------------------------------------------- */
    function queueSync(t)
    {
        if (!t.central) return;
        setActionQueue(prev => [...prev, { ...t, actionType: 'sync' }]);
    }

    /* -------------------------------------------------------------------------------- *\
    |                               queueDelete  — ajoute à la file sans exécuter
    \* -------------------------------------------------------------------------------- */
    function queueDelete(t)
    {
        if (!t.local) return;
        setActionQueue(prev => [...prev, { ...t, actionType: 'delete' }]);
    }

    /* -------------------------------------------------------------------------------- *\
    |                               needsSync  — track à synchroniser ?
    \* -------------------------------------------------------------------------------- */
    function needsSync(t)
    {
        if (!t.central) return false;          // rien à importer
        if (!t.local)   return true;           // pas présent en local
        return getstate(t) !== "same";         // différent (hash/distance/type)
    }

    /* -------------------------------------------------------------------------------- *\
    |                               syncAll  — refresh puis queue tous les tracks à sync
    \* -------------------------------------------------------------------------------- */
    function syncAll()
    {
        setLoading(true);
        track.getSynchro()
            .then((answer) =>
            {
                const newtracks = [];
                for (const t of answer.data)
                {
                    newtracks.push(
                    {
                        code: t.code,
                        sic: t.sic,
                        local: t.local,
                        central: t.central,
                        same: t.local && t.central && t.local.hash === t.central.hash && t.local.distance === t.central.distance && t.local.type === t.central.type,
                        state: getstate(t)
                    });
                }
                setTracks(newtracks);

                const toSync = newtracks.filter(needsSync);
                if (toSync.length === 0)
                {
                    message.info("All tracks are already synchronized");
                    return;
                }
                setActionQueue(prev => [...prev, ...toSync.map(t => ({ ...t, actionType: 'sync' }))]);
                message.success(`Queued ${toSync.length} track${toSync.length > 1 ? 's' : ''} to synchronize`);
            })
            .catch((error) => { message.error(error.message || "Internal error"); })
            .finally(() => setLoading(false));
    }

    /* -------------------------------------------------------------------------------- *\
    |                               processQueue  — exécute une action à la fois
    \* -------------------------------------------------------------------------------- */
    useEffect(() =>
    {
        if (currentAction !== null) return;   // déjà en cours
        if (actionQueue.length === 0) return; // file vide

        const next = actionQueue[0];
        setCurrentAction(next);
        setActionQueue(prev => prev.slice(1));
        hasProcessedRef.current = true;

        if (next.actionType === 'delete')
        {
            setTimeout(() =>
            {
                tracks_service.trackdelete(next.local.uuid)
                    .then((r) =>
                    {
                        if (r?.data?.error) message.error(r.data.error);
                        else
                        {
                            message.success(r?.data?.message || `Deleted ${next.sic} ${next.code}`);
                            setTracks(prev => prev.map(x => (x.sic === next.sic && x.code === next.code) ? { ...x, deleted: true } : x));
                        }
                    })
                    .catch((err) => { message.error(err?.response?.data?.message || "Internal error"); })
                    .finally(() => { setCurrentAction(null); });
            }, 2000);
        }
        else
        {
            tracks_service.synchronize(next.sic, next.code, next.central.distance, next.central.type)
                .then(() => { message.success(`Synchronized ${next.sic} ${next.code}`); })
                .catch((err) => { message.error(err?.response?.data?.message || "Internal error"); })
                .finally(() => { setCurrentAction(null); });
        }

    }, [actionQueue, currentAction]);

    /* -------------------------------------------------------------------------------- *\
    |                               refresh quand la file est vide
    \* -------------------------------------------------------------------------------- */
    useEffect(() =>
    {
        if (currentAction === null && actionQueue.length === 0 && hasProcessedRef.current)
        {
            hasProcessedRef.current = false;
            refreshAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAction, actionQueue]);

    useEffect(() =>
    {
        refreshAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <div className="header">
                <div className="page-infotracks-header">
                    <Button className="" onClick={refreshAll} disabled={isBusy}>Refresh</Button>
                    <Button type="primary" style={{marginLeft: 8}} onClick={syncAll} disabled={isBusy}>Synchro all</Button>
                    <Checkbox
                        checked={showDelete}
                        onChange={(e) => setShowDelete(e.target.checked)}
                        className="show-delete-checkbox"
                        style={{marginLeft: 15, display: 'inline-flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap'}}>
                        Show delete
                    </Checkbox>
                    { loading && <Loader/> }
                </div>
            </div>
            <div className="page-infotracks-body" style={{padding: 10, '--code-col-width': `${codeColWidth}px`}}>
                <div className="page-infotracks-mainheader">
                    <div className='page-infotracks-sep'>Local</div>
                    <div>Centralizer</div>
                </div>
                <div className="page-infotracks-line page-infotracks-header">
                    <div>SIC</div>
                    <div className='page-infotracks-sep'>Code</div>
                    <div>Distance</div>
                    <div>Type</div>
                    <div className='page-infotracks-sep'>Status</div>
                    <div>Distance</div>
                    <div className='page-infotracks-sep'>Type</div>
                    <div className='page-infotracks-sep'>Status</div>
                </div>
                {
                    tracks.map((t, i) =>
                    {
                        const inQueue = actionQueue.some(a => a.sic === t.sic && a.code === t.code);
                        const isActive = currentAction && currentAction.sic === t.sic && currentAction.code === t.code;
                        return (
                            <div key={'track' + i} className="page-infotracks-line page-infotracks-data">
                                <div>{t.sic}</div>
                                <div className='page-infotracks-sep'>{t.code}</div>

                                <div className={!t.local ? "page-infotracks-notfound" : ""}>{t.local?.distance}</div>
                                <div className={!t.local ? "page-infotracks-notfound" : ""}>{t.local?.type}</div>
                                <div className={(!t.local ? "page-infotracks-notfound" : "") + ' page-infotracks-sep'}>{t.local?.status}</div>

                                <div className={!t.central ? "page-infotracks-notfound" : ""}>{t.central?.distance}</div>
                                <div className={(!t.central ? "page-infotracks-notfound" : "") + ' page-infotracks-sep'}>{t.central?.type}</div>

                                <div className={(t.same ? "page-infotracks-same" : "page-infotracks-notsame") + ' page-infotracks-sep'}>{t.state}</div>
                                <div className="page-infotracks-actions">
                                    <Button size='small' disabled={t.deleted || inQueue || isActive || !t.local} onClick={() => downloadcsv(t, true)}>Download local</Button>
                                    { showDelete &&
                                        <Button size='small' type='primary' danger disabled={t.deleted || !t.local || inQueue || isActive} onClick={() => deleteLocal(t)}>
                                            { isActive ? <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} /> : inQueue ? 'Queued' : 'Delete local' }
                                        </Button>
                                    }
                                    <Button size='small' disabled={t.deleted || inQueue || isActive || !t.central} onClick={() => downloadcsv(t, false)}>Download central</Button>
                                    <Button size='small' disabled={t.deleted || !t.central || inQueue || isActive} onClick={() => queueSync(t)}>
                                        { isActive ? <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} /> : inQueue ? 'Queued' : 'Import from central' }
                                    </Button>
                                </div>
                            </div>
                        )
                    })
                }

                { isBusy &&
                    <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 100, padding: '8px 12px', background: '#f5f5f5', borderTop: '1px solid #d9d9d9', boxShadow: '0 -2px 6px rgba(0,0,0,0.08)' }}>
                        { currentAction && <span>Syncing: <b>{currentAction.sic} {currentAction.code}</b></span> }
                        { actionQueue.length > 0 && <span style={{ marginLeft: 12, color: '#888' }}>{actionQueue.length} action{actionQueue.length > 1 ? 's' : ''} remaining</span> }
                    </div>
                }
            </div>
            {
                loading &&
                <div className="page-infotracks-loading">
                    <div className="page-infotracks-cont">
                        <div><div>Working...</div><div>Please wait</div></div>
                        <div>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: 'black' }} spin />} />
                        </div>
                    </div>
                </div>
            }
        </div>
    );
}
